import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { 
    AttachmentObj, 
    fileActions,
    FileTypeCategory,
    ALLOWED_MIME_TYPES,
    FileValidationOptions,
    FileValidationResult,
    DEFAULT_MAX_FILE_SIZE
} from '../../../models/file-handler-model';
import {
    uploadFileMultipartWeb,
    uploadFileWithProgressWeb,
    downloadFileBinaryWeb,
    attachFileToCharger,
    getFileList,
    deleteFile
} from '../../../services/file-handler-service';

/**
 * FUNCTION: validateFile
 * PURPOSE: Validate file size and MIME type before upload
 * @param file - The file to validate
 * @param options - Validation options (max size, allowed types)
 * @returns FileValidationResult with validation status and error message
 */
export const validateFile = (
    file: File,
    options: FileValidationOptions = {}
): FileValidationResult => {
    const {
        maxSizeBytes = DEFAULT_MAX_FILE_SIZE,
        allowedTypes = FileTypeCategory.ANY,
        customMimeTypes = []
    } = options;

    // Check if file exists
    if (!file) {
        return { isValid: false, error: 'No file provided' };
    }

    // Check file size
    if (file.size === 0) {
        return { isValid: false, error: 'File is empty' };
    }

    if (file.size > maxSizeBytes) {
        const maxSizeMB = (maxSizeBytes / (1024 * 1024)).toFixed(2);
        return { 
            isValid: false, 
            error: `File size exceeds maximum of ${maxSizeMB}MB` 
        };
    }

    // Check MIME type if not allowing any type
    if (allowedTypes !== FileTypeCategory.ANY) {
        const allowedMimeTypes = customMimeTypes.length > 0 
            ? customMimeTypes 
            : ALLOWED_MIME_TYPES[allowedTypes];

        if (!allowedMimeTypes.includes(file.type)) {
            return { 
                isValid: false, 
                error: `Invalid file type. Allowed types: ${allowedMimeTypes.join(', ')}` 
            };
        }
    }

    // Validate file extension matches MIME type
    const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!extension) {
        return { isValid: false, error: 'File has no extension' };
    }

    return { isValid: true };
};

/**
 * FUNCTION: zipFile
 * PURPOSE: Compress a file using JSZip to reduce upload bandwidth
 * @param file - The file to compress
 * @param filename - Name to use for the file inside the zip
 * @returns Promise resolving to compressed Blob
 */
export const zipFile = async (file: File | Blob, filename: string): Promise<Blob> => {
    const zip = new JSZip();
    zip.file(filename, file);
    
    const zippedBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
            level: 6 // Balanced compression (1=fastest, 9=best compression)
        }
    });
    
    return zippedBlob;
};

/**
 * FUNCTION: unzipFile
 * PURPOSE: Decompress a zipped file
 * @param zippedBlob - The zipped blob to decompress
 * @param expectedFilename - Optional expected filename inside zip
 * @returns Promise resolving to { file: Blob, filename: string }
 */
export const unzipFile = async (
    zippedBlob: Blob, 
    expectedFilename?: string
): Promise<{ file: Blob; filename: string }> => {
    const zip = new JSZip();
    await zip.loadAsync(zippedBlob);
    
    // Get the first file in the zip, or use expected filename
    let targetFile: JSZip.JSZipObject | null = null;
    let filename = '';
    
    if (expectedFilename && zip.files[expectedFilename]) {
        targetFile = zip.files[expectedFilename];
        filename = expectedFilename;
    } else {
        // Get first file
        const files = Object.values(zip.files).filter(f => !f.dir);
        if (files.length > 0) {
            targetFile = files[0];
            filename = targetFile.name;
        }
    }
    
    if (!targetFile) {
        throw new Error('No file found in zip archive');
    }
    
    const fileBlob = await targetFile.async('blob');
    return { file: fileBlob, filename };
};

/**
 * FUNCTION: calculateFileHash
 * PURPOSE: Calculate SHA-256 hash of a file for integrity verification
 * @param file - The file to hash
 * @returns Promise resolving to hex-encoded SHA-256 hash
 */
export const calculateFileHash = async (file: File | Blob): Promise<string> => {
    try {
        // console.log('Calculating hash for file (web)...');
        
        // Get the binary data
        const arrayBuffer = await file.arrayBuffer();
        
        // Use Web Crypto API for proper binary hashing
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        
        // Convert to hex string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        
        console.log('Hash calculated successfully (web):', hashHex.substring(0, 16) + '...');
        return hashHex;
    } catch (error) {
        console.error('Error calculating file hash (web):', error);
        throw error;
    }
};

/**
 * FUNCTION: uploadFileToServer
 * PURPOSE: Upload a file to the server using multipart/form-data
 * @param file - The file to upload
 * @param attachObj - Attachment metadata
 * @param validationOptions - Optional validation rules
 * @returns Promise resolving to 0 on success, error string on failure
 */
export const uploadFileToServer = async (
    file: File,
    attachObj: AttachmentObj,
    validationOptions?: FileValidationOptions
): Promise<number | string> => {
    console.log(`Uploading file: ${file.name} (${file.size} bytes)`);
    
    try {
        // Validate file before upload
        const validation = validateFile(file, validationOptions);
        if (!validation.isValid) {
            console.error('File validation failed:', validation.error);
            return validation.error || 'File validation failed';
        }

        // Store original size
        const originalSize = file.size;
        attachObj.size = originalSize;

        // Compress file using JSZip
        console.log('Compressing file...');
        const zippedFile = await zipFile(file, file.name);
        const compressionRatio = ((1 - zippedFile.size / originalSize) * 100).toFixed(2);
        console.log(`File compressed: ${originalSize} bytes → ${zippedFile.size} bytes (${compressionRatio}% reduction)`);

        // Calculate SHA-256 hash of the ZIPPED file for integrity
        const fileHash = await calculateFileHash(zippedFile);
        attachObj.checksum = fileHash;

        console.log(`Zipped file hash (SHA-256): ${fileHash}`);

        // Create FormData for multipart upload with ZIPPED file
        const formData = new FormData();
        formData.append('file', zippedFile, `${file.name}.zip`);
        formData.append('action', fileActions.UPLOAD_FILE.toString());
        formData.append('documentname', attachObj.documentname);
        formData.append('checksum', fileHash);
        formData.append('size', originalSize.toString()); // Store original size
        formData.append('compressedsize', zippedFile.size.toString()); // Store compressed size
        formData.append('identifier', attachObj.identifier);
        formData.append('fileversion', attachObj.fileversion.toString());

        // Use service to upload file
        const result = await uploadFileMultipartWeb(formData);

        if (result.documentid) {
            attachObj.documentid = parseInt(result.documentid.toString(), 10);
            console.log(`File uploaded successfully. Document ID: ${attachObj.documentid}`);
            return 0; // Success
        } else if (result.error) {
            return result.error;
        }
        
        return 'Unknown error during upload';
    } catch (error) {
        console.error("Error uploading file to server:", error);
        return `Exception during upload: ${error}`;
    }
};

/**
 * FUNCTION: attachFileToChargerId
 * PURPOSE: Associate an uploaded file with a charger ID
 * @param attachObj - Attachment object with documentid and chargerid
 * @returns Promise resolving to 0 on success, error string on failure
 */
export const attachFileToChargerId = async (
    attachObj: AttachmentObj
): Promise<number | string> => {
    return await attachFileToCharger(attachObj);
};

/**
 * FUNCTION: downloadFilesFromServer
 * PURPOSE: Download a file from server using multipart response
 * @param attachObj - Attachment object with documentid or other identifiers
 * @param version - Optional version parameter ('original', 'thumbnail', 'medium')
 * @returns Promise that resolves when download is complete
 */
export const downloadFilesFromServer = async (
    attachObj: AttachmentObj,
    version?: 'original' | 'thumbnail' | 'medium'
): Promise<void> => {
    try {
        let body: any;
        
        if (attachObj.documentid > 0) {
            // Download based on documentid
            body = {
                action: fileActions.DOWNLOAD_FILE,
                docid: attachObj.documentid,
                ...(version && { version }) // Add version if provided
            };
        } else {
            // Download based on other identifiers
            body = {
                action: fileActions.DOWNLOAD_FILE,
                documentname: attachObj.documentname,
                identifier: attachObj.identifier,
                chargerid: attachObj.chargerid,
                fileversion: attachObj.fileversion,
                ...(version && { version }) // Add version if provided
            };
        }

        // Use service to download file
        const result = await downloadFileBinaryWeb('filehandler/download', body);

        if ('error' in result) {
            throw new Error(result.error);
        }

        const { blob, filename, hash } = result;
        
        // console.log(`Downloaded zipped file: ${blob.size} bytes`);
        
        // Verify hash if provided in response headers
        if (hash) {
            const clientHash = await calculateFileHash(blob);
            if (clientHash !== hash) {
                console.warn('File hash mismatch! File may be corrupted.');
                // Continue anyway, but warn user
            }
        }

        // Decompress the file
        // console.log('Decompressing file...');
        const { file: unzippedFile, filename: originalFilename } = await unzipFile(
            blob, 
            attachObj.documentname
        );
        
        // console.log(`File decompressed: ${blob.size} bytes → ${unzippedFile.size} bytes`);
        
        // Use the original filename from inside the zip
        const finalFilename = originalFilename || filename || attachObj.documentname;

        // Trigger download of the unzipped file
        saveAs(unzippedFile, finalFilename);
        
        // console.log(`Successfully downloaded and decompressed file: ${finalFilename}`);
    } catch (error) {
        console.error('Error while downloading file:', error);
        throw error;
    }
};

/**
 * FUNCTION: getListOfFiles
 * PURPOSE: Get a list of files associated with a charger
 * @param chargerid - The charger ID
 * @returns Promise resolving to array of AttachmentObj or null on error
 */
export const getListOfFiles = async (
    chargerid: string
): Promise<Array<AttachmentObj> | null> => {
    return await getFileList(chargerid);
};

/**
 * FUNCTION: deleteFileFromServer
 * PURPOSE: Mark a file as deleted (soft delete)
 * @param attachObj - Attachment object with documentid
 * @returns Promise resolving to true on success, false on failure
 */
export const deleteFileFromServer = async (
    attachObj: AttachmentObj
): Promise<boolean> => {
    return await deleteFile(attachObj);
};

/**
 * FUNCTION: uploadFileWithProgress
 * PURPOSE: Upload file with progress tracking
 * @param file - The file to upload
 * @param attachObj - Attachment metadata
 * @param onProgress - Callback for progress updates (0-100)
 * @param validationOptions - Optional validation rules
 * @returns Promise resolving to 0 on success, error string on failure
 */
export const uploadFileWithProgress = async (
    file: File,
    attachObj: AttachmentObj,
    onProgress?: (percent: number) => void,
    validationOptions?: FileValidationOptions
): Promise<number | string> => {
    console.log(`Uploading file with progress: ${file.name} (${file.size} bytes)`);
    
    try {
        // Validate file before upload
        const validation = validateFile(file, validationOptions);
        if (!validation.isValid) {
            console.error('File validation failed:', validation.error);
            return validation.error || 'File validation failed';
        }

        // Store original size
        const originalSize = file.size;
        attachObj.size = originalSize;

        // Compress file using JSZip
        console.log('Compressing file...');
        const zippedFile = await zipFile(file, file.name);
        const compressionRatio = ((1 - zippedFile.size / originalSize) * 100).toFixed(2);
        console.log(`File compressed: ${originalSize} bytes → ${zippedFile.size} bytes (${compressionRatio}% reduction)`);

        // Calculate SHA-256 hash of the ZIPPED file for integrity
        const fileHash = await calculateFileHash(zippedFile);
        attachObj.checksum = fileHash;

        // Create FormData for multipart upload with ZIPPED file
        const formData = new FormData();
        formData.append('file', zippedFile, `${file.name}.zip`);
        formData.append('action', fileActions.UPLOAD_FILE.toString());
        formData.append('documentname', attachObj.documentname);
        formData.append('checksum', fileHash);
        formData.append('size', originalSize.toString()); // Store original size
        formData.append('compressedsize', zippedFile.size.toString()); // Store compressed size
        formData.append('identifier', attachObj.identifier);
        formData.append('fileversion', attachObj.fileversion.toString());

        // Use service to upload file with progress
        const result = await uploadFileWithProgressWeb(formData, onProgress);

        if (result.documentid) {
            attachObj.documentid = parseInt(result.documentid.toString(), 10);
            console.log(`File uploaded successfully. Document ID: ${attachObj.documentid}`);
            return 0;
        } else if (result.error) {
            return result.error;
        }
        
        return 'Unknown error during upload';
    } catch (error) {
        console.error("Error uploading file with progress:", error);
        return `Exception during upload: ${error}`;
    }
};

